import { useCallback, useRef, useState, type FormEvent } from 'react';
import { ApiError, login, type AuthOptions, type LoginResult, type User } from '../api';
import { ChoosePassword } from './ChoosePassword';
import { Logo } from './icons';
import { TwoStepCode, TwoStepSetup } from './TwoStep';
import { BotCheckError, Turnstile, type BotCheckHandle } from './Turnstile';
import { PasswordField } from './PasswordField';

export function Login({
  notice,
  initialEmail = '',
  options,
  onLoggedIn,
  onForgot,
}: {
  notice?: string;
  initialEmail?: string;
  options: AuthOptions | null;
  onLoggedIn: (u: User, note?: string) => void;
  onForgot: (email: string) => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const siteKey = options?.turnstileSiteKey ?? null;
  const bot = useRef<BotCheckHandle>(null);
  // admins (two-step login): the password was right, the code step comes next
  const [twoStep, setTwoStep] = useState<{ mode: 'code' | 'setup'; ticket: string } | null>(null);
  // a temporary password from an admin (admin PR 3a): "Choose your password" first
  const [choose, setChoose] = useState<string | null>(null);
  const [stepNotice, setStepNotice] = useState<string | null>(null);
  const backToPassword = useCallback((message?: string) => {
    setTwoStep(null);
    setChoose(null);
    setPassword('');
    setBusy(false);
    setStepNotice(message ?? null);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // the bot check runs now, on submit (a token works once)
      const token = await bot.current?.run();
      next(await login(email.trim(), password, token));
    } catch (err) {
      // 400 / 401 / 429 messages come from the API as they should be shown
      setError(err instanceof ApiError || err instanceof BotCheckError ? err.message : 'Couldn’t log in. Try again.');
      setStepNotice(null);
      setBusy(false);
    }
  }

  /** Where the login goes next: a session, the two-step step, or choosing a password. */
  function next(r: LoginResult) {
    setStepNotice(null);
    if (r.kind === 'two-step') {
      setChoose(null);
      setTwoStep({ mode: r.mode, ticket: r.ticket });
    } else if (r.kind === 'new-password') {
      setChoose(r.ticket);
    } else onLoggedIn(r.user);
  }

  if (choose) {
    return (
      <main className="login">
        <div className="login-card">
          <Logo />
          <ChoosePassword ticket={choose} email={email.trim()} onNext={next} onOver={backToPassword} />
        </div>
      </main>
    );
  }

  if (twoStep) {
    return (
      <main className="login">
        <div className="login-card">
          <Logo />
          {twoStep.mode === 'code' ? (
            <TwoStepCode ticket={twoStep.ticket} email={email.trim()} onDone={onLoggedIn} onOver={backToPassword} />
          ) : (
            <TwoStepSetup ticket={twoStep.ticket} email={email.trim()} onDone={(u) => onLoggedIn(u)} onOver={backToPassword} />
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="login">
      <form className="login-card" onSubmit={submit} noValidate>
        <Logo />
        <h1>Log in to your phone</h1>
        {stepNotice && !error && <p className="login-error" role="alert">{stepNotice}</p>}
        {notice && !error && !stepNotice && <p className="login-notice">{notice}</p>}

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            required
            autoFocus={!initialEmail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <PasswordField
          id="login-password"
          label="Password"
          name="password"
          autoComplete="current-password"
          required
          autoFocus={!!initialEmail}
          value={password}
          onChange={setPassword}
          labelExtra={
            options?.passwordReset && (
              <button type="button" className="link-btn" onClick={() => onForgot(email.trim())}>
                Forgot password?
              </button>
            )
          }
        />

        <Turnstile ref={bot} siteKey={siteKey} action="login" onSolved={() => setError(null)} />

        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}

        <button className="btn btn-primary btn-block" type="submit" disabled={busy || !email || !password}>
          {busy ? 'Logging in…' : 'Log in'}
        </button>
        <p className="login-help">No account yet? Your administrator creates it for you.</p>
      </form>
    </main>
  );
}
